/** Rich-text handling for announcement bodies.
 *
 * Cloudflare Workers has no DOM/jsdom, so this is a small regex-based allowlist
 * sanitizer rather than a full HTML parser — appropriate because the only real
 * producer is our own Tiptap editor, which emits a small, known tag set. It still
 * guards against a compromised/malicious API caller submitting raw HTML directly,
 * since the output is later shown on an unauthenticated public page.
 */

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'a',
  'blockquote',
  'img',
])

const STRIP_ENTIRELY = ['script', 'style', 'iframe', 'object', 'embed', 'form', 'svg']

function stripTagsEntirely(html: string): string {
  let result = html
  for (const tag of STRIP_ENTIRELY) {
    const withContent = new RegExp(`<${tag}[^>]*>[\\s\\S]*?</${tag}>`, 'gi')
    const selfOrOpen = new RegExp(`<${tag}[^>]*/?>`, 'gi')
    result = result.replace(withContent, '').replace(selfOrOpen, '')
  }
  return result
}

function sanitizeAnchorAttributes(tag: string): string {
  const hrefMatch = tag.match(/href\s*=\s*(?:"([^"]*)"|'([^']*)')/i)
  const href = hrefMatch?.[1] ?? hrefMatch?.[2] ?? ''
  const isSafe = /^(https?:|mailto:)/i.test(href.trim())
  return isSafe ? `<a href="${href}" rel="noopener noreferrer" target="_blank">` : '<a>'
}

function sanitizeImgAttributes(tag: string): string {
  const srcMatch = tag.match(/src\s*=\s*(?:"([^"]*)"|'([^']*)')/i)
  const src = srcMatch?.[1] ?? srcMatch?.[2] ?? ''
  const trimmedSrc = src.trim()
  // Root-relative paths (single leading slash, not protocol-relative "//") cover local dev,
  // where the API is reached through a same-origin proxy rather than an absolute URL.
  const isSafe =
    /^https?:/i.test(trimmedSrc) || (trimmedSrc.startsWith('/') && !trimmedSrc.startsWith('//'))
  if (!isSafe) return ''

  const altMatch = tag.match(/alt\s*=\s*(?:"([^"]*)"|'([^']*)')/i)
  const alt = (altMatch?.[1] ?? altMatch?.[2] ?? '').replace(/"/g, '&quot;')
  return `<img src="${src}" alt="${alt}">`
}

export function sanitizeAnnouncementHtml(html: string): string {
  let result = stripTagsEntirely(html)

  result = result.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)(?:\s+[^<>]*)?>/g, (match, rawTag) => {
    const tag = rawTag.toLowerCase()
    const isClosing = match.startsWith('</')

    if (!ALLOWED_TAGS.has(tag)) return ''
    if (isClosing) return `</${tag}>`
    if (tag === 'a') return sanitizeAnchorAttributes(match)
    if (tag === 'img') return sanitizeImgAttributes(match)
    if (tag === 'br') return '<br>'
    return `<${tag}>`
  })

  return result.trim()
}

export function stripHtmlToText(html: string): string {
  return html
    .replace(/<(p|br|li|blockquote)\b[^>]*>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

export function htmlToExcerpt(html: string, maxLen = 150): string {
  const text = stripHtmlToText(html)
  return text.length > maxLen ? `${text.slice(0, maxLen - 1).trimEnd()}…` : text
}
